import {
	type QueryKey,
	type UseQueryOptions,
	useQuery,
} from "@tanstack/react-query";
import { useCallback } from "react";
import {
	type ApiError,
	type ApiRequestOptions,
	apiRequest,
} from "@/lib/api-client";
import { useAuth } from "@/lib/auth-ui";

type UseApiQueryParams<TData, TError = ApiError> = {
	queryKey: QueryKey;
	path: string;
	request?: Omit<ApiRequestOptions, "token">;
	queryOptions?: Omit<
		UseQueryOptions<TData, TError, TData>,
		"queryKey" | "queryFn"
	>;
};

export function useApiQuery<TData, TError = ApiError>({
	queryKey,
	path,
	request,
	queryOptions,
}: UseApiQueryParams<TData, TError>) {
	const { getToken } = useAuth();

	const queryFn = useCallback(async () => {
		const token = await getToken();
		return apiRequest<TData>(path, {
			...request,
			token: token ?? null,
			credentials: token ? "omit" : request?.credentials,
		});
	}, [getToken, path, request]);

	return useQuery<TData, TError>({
		queryKey,
		queryFn,
		...queryOptions,
	});
}
